/**
 * @deprecated URDB support has been removed.
 *
 * Site DNA now uses the Vortx Flux Rate Database exclusively.
 * All tariffs are either:
 * 1. Blended rates (simple $/kWh + $/kW)
 * 2. TOU rates (8760 hourly energy rates from admin-approved tariffs)
 *
 * This file is kept for backward compatibility with existing scenarios
 * that may have URDB-sourced tariff data. New code should NOT use this file.
 *
 * Migration: Use tariffTransformers.js for all tariff operations.
 *
 * ============================================================================
 * LEGACY CODE BELOW - DO NOT USE FOR NEW FEATURES
 * ============================================================================
 *
 * URDB to Custom Tariff Transformation Utility
 *
 * Transforms URDB tariff data into our custom format for:
 * 1. Local storage and caching
 * 2. Direct solver integration (no URDB polling)
 * 3. Building a tariff library
 */

// Log deprecation warning in development
if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
  console.warn(
    '[DEPRECATED] urdbToCustomTariff.js is deprecated. ' +
    'URDB support has been removed. Use tariffTransformers.js instead.'
  );
}

/**
 * Detect the rate mode from URDB tariff structure
 * @param {Object} urdbTariff - Raw URDB tariff data
 * @returns {'simple' | 'tiered' | 'tou'}
 */
function detectRateMode(urdbTariff) {
    const energyStructure = urdbTariff.energyratestructure || [];

    // Check if TOU (multiple periods in schedule)
    const hasMultiplePeriods = energyStructure.length > 1;
    const hasWeekdaySchedule = urdbTariff.energyweekdayschedule?.some(
        month => month?.some(hour => hour > 0)
    );

    if (hasMultiplePeriods && hasWeekdaySchedule) {
        return 'tou';
    }

    // Check if tiered (multiple tiers in any period)
    const hasTiers = energyStructure.some(period =>
        Array.isArray(period) && period.length > 1
    );

    if (hasTiers) {
        return 'tiered';
    }

    return 'simple';
}

/**
 * Calculate blended energy rate from URDB structure
 * @param {Object} urdbTariff - Raw URDB tariff data
 * @returns {number} Blended $/kWh rate
 */
function calculateBlendedEnergyRate(urdbTariff) {
    const energyStructure = urdbTariff.energyratestructure || [];

    if (energyStructure.length === 0) {
        throw new Error('URDB tariff missing energyratestructure - cannot calculate blended energy rate');
    }

    // Collect all rates
    const rates = [];
    energyStructure.forEach(period => {
        if (Array.isArray(period)) {
            period.forEach(tier => {
                if (tier && typeof tier.rate === 'number') {
                    rates.push(tier.rate);
                }
            });
        }
    });

    if (rates.length === 0) {
        // Try flat rate if structured rates not found
        if (typeof urdbTariff.flatdemandstructure?.[0]?.[0]?.rate === 'number') {
            return urdbTariff.flatdemandstructure[0][0].rate;
        }
        throw new Error('URDB tariff has no valid energy rates in energyratestructure');
    }

    // Simple average (can be weighted by schedule later)
    return rates.reduce((sum, r) => sum + r, 0) / rates.length;
}

/**
 * Calculate blended demand rate from URDB structure
 * @param {Object} urdbTariff - Raw URDB tariff data
 * @returns {number} Blended $/kW rate
 */
function calculateBlendedDemandRate(urdbTariff) {
    const demandStructure = urdbTariff.demandratestructure || [];
    const flatStructure = urdbTariff.flatdemandstructure || [];

    const rates = [];

    // Collect from demand structure
    demandStructure.forEach(period => {
        if (Array.isArray(period)) {
            period.forEach(tier => {
                if (tier && typeof tier.rate === 'number') {
                    rates.push(tier.rate);
                }
            });
        }
    });

    // Collect from flat demand structure
    flatStructure.forEach(period => {
        if (Array.isArray(period)) {
            period.forEach(tier => {
                if (tier && typeof tier.rate === 'number') {
                    rates.push(tier.rate);
                }
            });
        }
    });

    if (rates.length === 0) {
        // Demand rates are optional for some tariffs - return 0 as explicit "no demand charges"
        return 0;
    }

    return rates.reduce((sum, r) => sum + r, 0) / rates.length;
}

/**
 * Extract energy tiers for tiered rate mode
 * @param {Object} urdbTariff - Raw URDB tariff data
 * @returns {Array} Tier definitions
 */
function extractEnergyTiers(urdbTariff) {
    const energyStructure = urdbTariff.energyratestructure || [];

    if (energyStructure.length === 0 || !Array.isArray(energyStructure[0])) {
        return [];
    }

    // Use first period's tiers as baseline
    const firstPeriod = energyStructure[0];
    return firstPeriod.map((tier, idx) => ({
        id: idx + 1,
        maxKwh: tier.max || (idx === firstPeriod.length - 1 ? Infinity : 1000 * (idx + 1)),
        rate: tier.rate || 0,
        adj: tier.adj || 0
    }));
}

/**
 * Extract TOU schedule data for TOUBuilder component
 * @param {Object} urdbTariff - Raw URDB tariff data
 * @returns {Object} TOU configuration matching TOUBuilder's expected format
 */
function extractTOUData(urdbTariff) {
    const energyStructure = urdbTariff.energyratestructure || [];
    const demandStructure = urdbTariff.demandratestructure || [];
    const weekdaySchedule = urdbTariff.energyweekdayschedule || [];
    const weekendSchedule = urdbTariff.energyweekendschedule || [];

    // Analyze schedule to determine which periods are used in which months
    // This helps identify seasonal patterns (summer vs winter)
    const periodMonthUsage = {};
    weekdaySchedule.forEach((monthSchedule, monthIdx) => {
        if (monthSchedule) {
            monthSchedule.forEach(periodId => {
                if (!periodMonthUsage[periodId]) {
                    periodMonthUsage[periodId] = { summer: 0, winter: 0 };
                }
                // Summer: June-Sept (5-8), Winter: others
                if (monthIdx >= 5 && monthIdx <= 8) {
                    periodMonthUsage[periodId].summer++;
                } else {
                    periodMonthUsage[periodId].winter++;
                }
            });
        }
    });

    // Sort periods by rate to determine off-peak, mid-peak, on-peak
    const periodRates = energyStructure.map((period, idx) => ({
        idx,
        rate: period?.[0]?.rate || 0
    })).sort((a, b) => a.rate - b.rate);

    // Assign TOU categories based on relative rates
    const periodCategories = {};
    const numPeriods = periodRates.length;
    periodRates.forEach((p, sortIdx) => {
        if (numPeriods <= 3) {
            // Simple: off-peak, mid-peak, on-peak
            periodCategories[p.idx] = sortIdx === 0 ? 'Off-Peak' :
                sortIdx === numPeriods - 1 ? 'On-Peak' : 'Mid-Peak';
        } else {
            // With seasonality: determine season first, then TOU
            const usage = periodMonthUsage[p.idx] || { summer: 0, winter: 0 };
            const season = usage.summer > usage.winter ? 'Summer' : 'Winter';

            // Group by rate tier within season
            const tierPct = sortIdx / (numPeriods - 1);
            let touType = tierPct <= 0.33 ? 'Off-Peak' : tierPct <= 0.66 ? 'Mid-Peak' : 'On-Peak';

            periodCategories[p.idx] = `${season} ${touType}`;
        }
    });

    // Build period definitions - TOUBuilder expects { id, name, energy, demand }
    const periods = energyStructure.map((period, idx) => {
        const energyRate = period?.[0]?.rate || 0;
        const demandRate = demandStructure?.[idx]?.[0]?.rate || 0;

        return {
            id: idx,
            name: periodCategories[idx] || `Period ${idx}`,
            energy: energyRate,
            demand: demandRate
        };
    });

    // Build comprehensive schedule windows from URDB schedule matrices
    // Analyze each month's schedule and group similar patterns
    const windows = [];
    let windowId = 0;

    // Group months by similar hourly patterns
    const monthPatterns = {};
    weekdaySchedule.forEach((hourlySchedule, monthIdx) => {
        if (!hourlySchedule) return;
        const patternKey = JSON.stringify(hourlySchedule);
        if (!monthPatterns[patternKey]) {
            monthPatterns[patternKey] = {
                months: [],
                schedule: hourlySchedule
            };
        }
        monthPatterns[patternKey].months.push(monthIdx);
    });

    // For each unique pattern, extract time windows
    Object.values(monthPatterns).forEach(pattern => {
        const { months, schedule } = pattern;

        // Find contiguous hour blocks for each period
        let currentPeriod = schedule[0];
        let startHour = 0;

        for (let hour = 1; hour <= 24; hour++) {
            const periodId = hour < 24 ? schedule[hour] : -1;

            if (periodId !== currentPeriod || hour === 24) {
                // Create window for current period block
                // Skip period 0 as it's the base/default period
                if (currentPeriod > 0) {
                    windows.push({
                        id: windowId++,
                        periodId: currentPeriod,
                        startHour,
                        endHour: hour,
                        months: [...months],
                        weekdays: true,
                        weekends: false
                    });
                }
                startHour = hour;
                currentPeriod = periodId;
            }
        }
    });

    // Also analyze weekend schedules if different
    const weekendPatterns = {};
    weekendSchedule.forEach((hourlySchedule, monthIdx) => {
        if (!hourlySchedule) return;
        const patternKey = JSON.stringify(hourlySchedule);
        if (!weekendPatterns[patternKey]) {
            weekendPatterns[patternKey] = {
                months: [],
                schedule: hourlySchedule
            };
        }
        weekendPatterns[patternKey].months.push(monthIdx);
    });

    Object.values(weekendPatterns).forEach(pattern => {
        const { months, schedule } = pattern;

        let currentPeriod = schedule[0];
        let startHour = 0;

        for (let hour = 1; hour <= 24; hour++) {
            const periodId = hour < 24 ? schedule[hour] : -1;

            if (periodId !== currentPeriod || hour === 24) {
                if (currentPeriod > 0) {
                    windows.push({
                        id: windowId++,
                        periodId: currentPeriod,
                        startHour,
                        endHour: hour,
                        months: [...months],
                        weekdays: false,
                        weekends: true
                    });
                }
                startHour = hour;
                currentPeriod = periodId;
            }
        }
    });

    // Fixed monthly fee and facility demand from URDB
    const fixedMonthlyFee = urdbTariff.fixedmonthlycharge || 0;

    // Extract facility demand - can be in flatdemandstructure or as separate flat rate
    let facilityDemandRate = 0;
    const flatDemand = urdbTariff.flatdemandstructure;
    if (flatDemand && Array.isArray(flatDemand)) {
        // Sum all flat demand rates (there may be multiple seasons)
        flatDemand.forEach(season => {
            if (Array.isArray(season) && season[0]?.rate) {
                facilityDemandRate = Math.max(facilityDemandRate, season[0].rate);
            }
        });
    }

    return {
        periods,
        windows,
        fixedMonthlyFee,
        facilityDemandRate,
        // Keep raw schedules for reference
        weekdaySchedule,
        weekendSchedule,
        demandPeriods: urdbTariff.demandweekdayschedule || [],
        demandRates: demandStructure.map(p => p?.[0]?.rate || 0)
    };
}

/**
 * Transform URDB tariff to custom format
 * @param {Object} urdbTariff - Raw URDB tariff data
 * @returns {Object} Custom tariff format ready for storage
 */
export function urdbToCustomTariff(urdbTariff) {
    if (!urdbTariff) {
        throw new Error('No URDB tariff provided');
    }

    const rateMode = detectRateMode(urdbTariff);
    const blendedEnergyRate = calculateBlendedEnergyRate(urdbTariff);
    const blendedDemandRate = calculateBlendedDemandRate(urdbTariff);

    // Build the custom tariff object
    const customTariff = {
        // Metadata
        name: `${urdbTariff.utility || 'Unknown Utility'} - ${urdbTariff.name || 'Rate'}`,
        utility_name: urdbTariff.utility || '',
        rate_schedule_name: urdbTariff.name || '',
        source: 'urdb',
        urdb_label: urdbTariff.label || null,

        // Rate configuration
        rate_mode: rateMode,

        // Rate data for solver and UI
        rate_data: {
            // Blended rates (always computed)
            blendedEnergyRate,
            blendedDemandRate,

            // Fixed charges
            fixedMonthlyCharge: urdbTariff.fixedmonthlycharge || 0,
            minMonthlyCharge: urdbTariff.minmonthlycharge || 0,
            annualMinCharge: urdbTariff.annualmincharge || 0,

            // Tiered rates
            enableTieredRates: rateMode === 'tiered',
            energyTiers: rateMode === 'tiered' ? extractEnergyTiers(urdbTariff) : [],

            // TOU data
            touData: rateMode === 'tou' ? extractTOUData(urdbTariff) : null,

            // Full URDB structures for solver
            energyratestructure: urdbTariff.energyratestructure || [],
            demandratestructure: urdbTariff.demandratestructure || [],
            flatdemandstructure: urdbTariff.flatdemandstructure || [],
            energyweekdayschedule: urdbTariff.energyweekdayschedule || [],
            energyweekendschedule: urdbTariff.energyweekendschedule || [],
            demandweekdayschedule: urdbTariff.demandweekdayschedule || [],
            demandweekendschedule: urdbTariff.demandweekendschedule || [],

            // Additional URDB fields for completeness
            energycomments: urdbTariff.energycomments || '',
            demandcomments: urdbTariff.demandcomments || '',
            demandunits: urdbTariff.demandunits || 'kW',

            // Original URDB data for reference
            urdb_original: {
                label: urdbTariff.label,
                utility: urdbTariff.utility,
                name: urdbTariff.name,
                sector: urdbTariff.sector,
                startdate: urdbTariff.startdate,
                enddate: urdbTariff.enddate,
                description: urdbTariff.description
            }
        }
    };

    return customTariff;
}

/**
 * Convert custom tariff back to solver-compatible format
 * @param {Object} customTariff - Our custom tariff format
 * @returns {Object} ElectricTariff object for solver
 */
export function customTariffToSolverFormat(customTariff) {
    const rateData = customTariff.rate_data || {};

    return {
        // Use full rate structures instead of urdb_label
        energyratestructure: rateData.energyratestructure || [],
        demandratestructure: rateData.demandratestructure || [],
        flatdemandstructure: rateData.flatdemandstructure || [],
        energyweekdayschedule: rateData.energyweekdayschedule || [],
        energyweekendschedule: rateData.energyweekendschedule || [],
        demandweekdayschedule: rateData.demandweekdayschedule || [],
        demandweekendschedule: rateData.demandweekendschedule || [],

        // Fallback blended rates
        blended_annual_energy_rate: rateData.blendedEnergyRate,
        blended_annual_demand_rate: rateData.blendedDemandRate,

        // Fixed charges
        fixedmonthlycharge: rateData.fixedMonthlyCharge || 0,
        minmonthlycharge: rateData.minMonthlyCharge || 0,
        annual_min_charge: rateData.annualMinCharge || 0,

        // Metadata (for reference, not used by solver)
        urdb_utility_name: customTariff.utility_name,
        urdb_rate_name: customTariff.rate_schedule_name,
        urdb_label: customTariff.urdb_label // Keep for reference only
    };
}

export default urdbToCustomTariff;
